/**
 * The sidebar's catalogue, and the operator's arrangement of it.
 *
 * The catalogue is the list of links every user's sidebar is built from —
 * each with the permissions and addon that decide whether that user sees it.
 * It lives here rather than in Sidebar.vue because two screens need it: the
 * sidebar, which renders it, and the Sidebar tab of Configurations, which lets
 * the operator rearrange it and has to show every entry, not only the ones the
 * operator happens to be allowed to open.
 *
 * The arrangement is three configuration rows, shared by every user of the
 * installation and edited on that tab:
 *
 *   sidebar_layout        'grouped' | 'flat'           default 'grouped'
 *   sidebar_group_titles  {"orders": "Trading", …}     default {}
 *   sidebar_order         {"grouped": {"top": [...], "children": {"orders": [...]}},
 *                          "flat": [...]}              default {}
 *
 * `grouped` is the rail as designed: collapsible groups with their links
 * inside. `flat` dissolves the groups so every link sits at the top level.
 * Each layout keeps its own order, because the sequence that reads well with
 * groups is not the one that reads well without them.
 *
 * Ids: a link is its route `name`; a group is `group:<key>`. Both orders are
 * applied by arrangeNavigation(), which drops an id it does not know and
 * appends, in catalogue order, anything the rows leave out — so a link added
 * by a later release appears without the operator revisiting the tab, and a
 * row written against an older release never hides one. A group can be
 * renamed but not removed: a blank title falls back to the catalogue's.
 */

import {
    AdjustmentsHorizontalIcon,
    ArchiveBoxIcon,
    ArrowDownTrayIcon,
    ArrowPathIcon,
    ArrowTrendingUpIcon,
    ArrowUpTrayIcon,
    ArrowUturnLeftIcon,
    ArrowUturnUpIcon,
    ArrowsRightLeftIcon,
    BanknotesIcon,
    BellAlertIcon,
    BuildingOffice2Icon,
    BuildingStorefrontIcon,
    ChartBarIcon,
    CircleStackIcon,
    ClipboardDocumentListIcon,
    Cog6ToothIcon,
    ComputerDesktopIcon,
    CreditCardIcon,
    CubeIcon,
    ExclamationTriangleIcon,
    GlobeAltIcon,
    HomeIcon,
    IdentificationIcon,
    MapIcon,
    QueueListIcon,
    ReceiptRefundIcon,
    RectangleGroupIcon,
    ScaleIcon,
    ShieldCheckIcon,
    ShoppingCartIcon,
    Square3Stack3DIcon,
    TicketIcon,
    TruckIcon,
    UserCircleIcon,
    UserGroupIcon,
    UsersIcon,
    XCircleIcon,
} from '@heroicons/vue/24/outline';

export const SIDEBAR_LAYOUT_KEY = 'sidebar_layout';
export const SIDEBAR_GROUP_TITLES_KEY = 'sidebar_group_titles';
export const SIDEBAR_ORDER_KEY = 'sidebar_order';
export const SIDEBAR_LAYOUTS = ['grouped', 'flat'];

// Entries with `children` render as a collapsible group; everything else is a
// flat top-level link. This array's order is the default order in both
// layouts. A group's `key` is what its expanded/collapsed state persists
// under, and what its renamed title and its arrangement are keyed on — so a
// key is forever, a title is not.
export const navigation = [
    { title: 'Dashboard', name: 'dashboard', icon: HomeIcon },
    // Ask is deliberately NOT in this rail. It is reached from the floating
    // button in the bottom-right corner, which is on every page including this
    // one — a question is something you have while looking at something else,
    // and a rail entry that navigates away from what prompted it is the wrong
    // shape for that. See AskLauncher.vue for the three switches that decide
    // whether somebody sees the button.
    { title: 'POS', name: 'pos', icon: ComputerDesktopIcon, permissions: ['manage_sales'] },
    {
        title: 'Orders',
        key: 'orders',
        icon: ReceiptRefundIcon,
        children: [
            { title: 'Sales', name: 'sales', icon: ScaleIcon, permissions: ['manage_sales'] },
            {
                title: 'Multi Sales',
                name: 'multi_sales',
                icon: QueueListIcon,
                permissions: ['multi_sale_generate', 'multi_sale_complete'],
                addon: 'multi_sale',
            },
            { title: 'Purchases', name: 'purchases', icon: ShoppingCartIcon, permissions: ['manage_purchases'] },
            { title: 'Order Requests', name: 'RequestOrderList', icon: ClipboardDocumentListIcon, permissions: ['view_order_requests'] },
            { title: 'Refund & Return', name: 'refund_return', icon: ArrowUturnLeftIcon, permissions: ['manage_refunds'] },
        ],
    },
    {
        title: 'Inventory',
        key: 'inventory',
        icon: CubeIcon,
        children: [
            { title: 'Products', name: 'products', icon: Square3Stack3DIcon, permissions: ['manage_products'] },
            { title: 'Transfers', name: 'transfers', icon: ArrowsRightLeftIcon, permissions: ['manage_transfer'] },
            { title: 'Damaged Items', name: 'damaged_items', icon: XCircleIcon, permissions: ['manage_damaged_items'] },
            { title: 'Supplier Returns', name: 'supplier_returns', icon: ArrowUturnUpIcon, permissions: ['manage_supplier_returns'] },
            {
                title: 'Replenishment',
                name: 'replenishment',
                icon: ArrowPathIcon,
                permissions: ['manage_replenishment'],
                addon: 'replenishment_brain',
            },
            {
                title: 'Shelf Map',
                name: 'shelf_map',
                icon: MapIcon,
                permissions: ['manage_shelf_map', 'manage_sales', 'manage_inventory', 'manage_products'],
                addon: 'shelf_map',
            },
            {
                title: 'Stock Atlas',
                name: 'stock_atlas',
                icon: GlobeAltIcon,
                permissions: ['manage_shelf_map', 'manage_sales', 'manage_inventory', 'manage_products'],
                addon: 'stock_atlas',
            },
        ],
    },
    { title: 'Expenses', name: 'expenses', icon: BanknotesIcon, permissions: ['manage_expenses'] },
    // The finance side of card machines: acquirers, settlements, chargebacks.
    // The cashier's half — declaring a batch — lives on the branch page and
    // needs only use_card_machines, so the rail entry is the manager's.
    {
        title: 'Card Machines',
        name: 'card-machines',
        icon: CreditCardIcon,
        permissions: ['manage_card_machines'],
        addon: 'card_machines',
    },
    {
        title: 'Cash Runway',
        name: 'cash_runway',
        icon: ArrowTrendingUpIcon,
        permissions: ['view_cash_runway', 'manage_cash_runway'],
        addon: 'cash_tax_runway',
    },
    {
        title: 'Contacts',
        key: 'contacts',
        icon: UserCircleIcon,
        children: [
            { title: 'Customers', name: 'customers', icon: UsersIcon, permissions: ['manage_customers', 'manage_wholesalers'] },
            { title: 'Suppliers', name: 'suppliers', icon: BuildingStorefrontIcon, permissions: ['manage_suppliers'] },
            {
                title: 'Automotive',
                name: 'automotives',
                icon: TruckIcon,
                permissions: ['manage_automotives'],
                addon: 'automotive_management',
            },
            {
                title: 'Reminders',
                name: 'reminders',
                icon: BellAlertIcon,
                permissions: ['manage_sales', 'manage_customers', 'manage_wholesalers'],
                addon: 'customer_reminders',
            },
        ],
    },
    {
        title: 'Dispatch',
        key: 'dispatch',
        icon: MapIcon,
        children: [
            { title: 'Gate Passes', name: 'gate_passes', icon: TicketIcon, permissions: ['manage_gate_passes'] },
            { title: 'Load Forms', name: 'load_forms', icon: TruckIcon, permissions: ['manage_load_forms'], addon: 'load_form_delivery' },
            {
                title: 'Delivery People',
                name: 'delivery_persons',
                icon: IdentificationIcon,
                permissions: ['manage_load_forms', 'manage_gate_passes'],
            },
        ],
    },
    {
        title: 'Setup',
        key: 'setup',
        icon: Cog6ToothIcon,
        children: [
            { title: 'Branches', name: 'branches', icon: BuildingOffice2Icon, permissions: ['manage_branches'] },
            {
                title: 'Attributes',
                name: 'attributes',
                icon: RectangleGroupIcon,
                permissions: [
                    'manage_categories',
                    'manage_units',
                    'manage_sizes',
                    'manage_materials',
                    'manage_colors',
                    'manage_types',
                    'manage_accounts',
                ],
            },
            {
                title: 'Workers',
                name: 'workers',
                icon: UserGroupIcon,
                permissions: ['manage_workers'],
                addon: 'service_workers',
            },
        ],
    },
    {
        title: 'Data',
        key: 'data',
        icon: CircleStackIcon,
        children: [
            // Labelled without "Stock" because the full name truncates on an
            // indented row; the route and page title are unchanged.
            { title: 'Discrepancies', name: 'stock_discrepancies', icon: ExclamationTriangleIcon, permissions: ['manage_stock_discrepancies'] },
            { title: 'Imports', name: 'imports', icon: ArrowUpTrayIcon, permissions: ['import_data'] },
            { title: 'Exports', name: 'exports', icon: ArrowDownTrayIcon, permissions: ['export_data'], addon: 'datatable_export' },
            { title: 'Trash', name: 'trash', icon: ArchiveBoxIcon, permissions: ['manage_trash'] },
        ],
    },
    // Mirrors the reports route's permission list: the hub shows when the user
    // can open at least one report, and the page itself filters the tiles.
    {
        title: 'Reports',
        name: 'reports',
        icon: ChartBarIcon,
        permissions: [
            'view sale report',
            'view purchase report',
            'view expense report',
            'view_customer_report',
            'view_supplier_report',
            'view_product_report',
            'view_variant_report',
            'view_tax_report',
            'view_profit_loss_report',
            'view_refund_report',
            'view_transfer_report',
            'view_branch_report',
            'manage_ledger',
            'manage_refunds',
            'manage_damaged_items',
            'manage_supplier_returns',
            'manage_automotives',
            'view_cash_runway',
            'manage_tills',
            'manage_card_machines',
        ],
    },
];

// The "Admin Navigation" block under the rail. Not part of the arrangement:
// it is a labelled section rather than a group, and it stays where it is in
// both layouts.
export const managerNavigation = [
    { title: 'User Management', name: 'manage_users', icon: UserGroupIcon, roles: ['admin'], permissions: ['manage_users', 'manage_roles'] },
    {
        title: 'Configurations',
        name: 'manage_configurations',
        icon: AdjustmentsHorizontalIcon,
        roles: ['admin'],
        permissions: ['manage_configurations'],
    },
    {
        title: 'Audit Log',
        name: 'audit_log',
        icon: ShieldCheckIcon,
        roles: ['admin'],
        permissions: ['view_audit_log'],
    },
    // The vendor's bill for the software. No addon: it is armed by the
    // presence of terms in `billing_plans`, which is why the flag below is
    // not one. An installation nobody is charging for has nothing to show
    // here — the software is simply free to them — so the entry is hidden
    // rather than leading to a page that says so.
    {
        title: 'Subscription',
        name: 'billing',
        icon: CreditCardIcon,
        roles: ['admin'],
        permissions: ['manage_billing'],
        requiresBillingTerms: true,
    },
];

/** The id an order row refers to an entry by: a link by its route, a group by its key. */
export const navigationEntryId = (entry) => (entry.children ? `group:${entry.key}` : entry.name);

/** Every link in catalogue order, groups dissolved — what the flat layout arranges. */
export const flattenNavigation = (entries = navigation) => entries.flatMap((entry) => (entry.children ? entry.children : [entry]));

/**
 * A configuration row holding a JSON object, or {} for anything else. The
 * rows are written by the settings screen and validated as JSON on the way
 * in, but a hand-edited or truncated one must degrade to "as designed", not
 * to a sidebar that fails to render.
 */
function jsonObject(value) {
    try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
}

const idList = (value) => (Array.isArray(value) ? value.filter((id) => typeof id === 'string') : []);

/**
 * The group titles exactly as stored — untrimmed, so the settings screen can
 * echo a title back into its input while it is being typed. Anything that is
 * not a string is dropped.
 */
export function storedGroupTitles(configurations = {}) {
    const titles = {};
    for (const [key, title] of Object.entries(jsonObject((configurations ?? {})[SIDEBAR_GROUP_TITLES_KEY]))) {
        if (typeof title === 'string') titles[key] = title;
    }
    return titles;
}

/**
 * The three rows read as what they mean. An unrecognised layout is the
 * default; a title that is blank or not a string is dropped, so the
 * catalogue's own shows; an order that is not a list of ids is empty.
 */
export function resolveSidebarSettings(configurations = {}) {
    const config = configurations ?? {};
    const layout = String(config[SIDEBAR_LAYOUT_KEY] ?? '')
        .trim()
        .toLowerCase();

    const groupTitles = {};
    for (const [key, title] of Object.entries(storedGroupTitles(config))) {
        if (title.trim()) groupTitles[key] = title.trim();
    }

    const order = jsonObject(config[SIDEBAR_ORDER_KEY]);
    const grouped = jsonObject(order.grouped);
    const children = {};
    for (const [key, ids] of Object.entries(jsonObject(grouped.children))) {
        children[key] = idList(ids);
    }

    return {
        layout: SIDEBAR_LAYOUTS.includes(layout) ? layout : 'grouped',
        groupTitles,
        order: {
            grouped: { top: idList(grouped.top), children },
            flat: idList(order.flat),
        },
    };
}

/**
 * `entries` in the order `ids` gives, by `idOf`. An id that names nothing is
 * skipped, a repeated id counts once, and every entry the ids leave out
 * follows in its own order — so a stored order can be stale in either
 * direction without losing a link.
 */
export function orderEntries(entries, ids, idOf) {
    const byId = new Map(entries.map((entry) => [idOf(entry), entry]));
    const placed = new Set();
    const ordered = [];

    for (const id of ids) {
        const entry = byId.get(id);
        if (entry && !placed.has(id)) {
            ordered.push(entry);
            placed.add(id);
        }
    }
    for (const entry of entries) {
        if (!placed.has(idOf(entry))) ordered.push(entry);
    }

    return ordered;
}

/**
 * The entries a viewer can use: links that pass `isAvailable`, and groups
 * with at least one such link — so a group never shows as an empty
 * disclosure and never hides a link the viewer is allowed to open. The
 * sidebar passes permissions and addons; the settings screen passes addons
 * alone, because the operator arranges pages for everyone.
 */
export function availableNavigation(entries, isAvailable) {
    return entries.reduce((acc, entry) => {
        if (!entry.children) {
            if (isAvailable(entry)) acc.push(entry);
            return acc;
        }
        const children = entry.children.filter(isAvailable);
        if (children.length) acc.push({ ...entry, children });
        return acc;
    }, []);
}

/** `ids` with `id` moved `delta` places, or the same array when it cannot move that far. */
export function moveId(ids, id, delta) {
    const from = ids.indexOf(id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ids.length || delta === 0) return ids;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, id);
    return next;
}

/**
 * `fullIds` with the ids in `visibleOrder` rearranged into that order, each
 * taking one of the slots those ids already occupy. The settings screen
 * shows only the pages the installation's addons provide, but the stored
 * order carries every page: this is how a page that is not shown keeps its
 * place instead of drifting to the end each time the shown ones are moved.
 * An id the full list does not have is ignored.
 */
export function placeVisible(fullIds, visibleOrder) {
    const known = new Set(fullIds);
    const order = visibleOrder.filter((id) => known.has(id));
    const shown = new Set(order);
    const slots = [];
    fullIds.forEach((id, index) => {
        if (shown.has(id)) slots.push(index);
    });
    const next = [...fullIds];
    order.forEach((id, position) => {
        next[slots[position]] = id;
    });
    return next;
}

/** A group's title as the operator renamed it, or as the catalogue has it. */
export const groupTitle = (group, settings) => settings?.groupTitles?.[group.key] || group.title;

/**
 * The catalogue arranged as the settings say: the rail's entries in order,
 * each group carrying its shown title and its children in order — or, in the
 * flat layout, every link at the top level in the flat order. Permissions and
 * addons are not applied here; the sidebar filters what it renders and the
 * settings screen shows everything.
 */
export function arrangeNavigation(settings, entries = navigation) {
    const resolved = settings ?? resolveSidebarSettings({});

    if (resolved.layout === 'flat') {
        return orderEntries(flattenNavigation(entries), resolved.order.flat, (entry) => entry.name);
    }

    return orderEntries(entries, resolved.order.grouped.top, navigationEntryId).map((entry) =>
        entry.children
            ? {
                  ...entry,
                  title: groupTitle(entry, resolved),
                  children: orderEntries(entry.children, resolved.order.grouped.children[entry.key] ?? [], (child) => child.name),
              }
            : entry,
    );
}

/**
 * The rows the settings screen writes for an arrangement, so the sequence is
 * explicit rather than "whatever the catalogue did last". A group title is
 * stored as typed — untrimmed, because this runs on every keystroke and a
 * trailing space has to survive until the next letter — and a blank one is
 * not stored at all, which is how a group goes back to its catalogue title.
 */
export function serializeSidebarSettings({ layout, groupTitles = {}, groupedTop, groupedChildren = {}, flat }) {
    const titles = {};
    for (const group of navigation) {
        if (!group.children) continue;
        const title = String(groupTitles[group.key] ?? '');
        if (title.trim()) titles[group.key] = title;
    }

    return {
        [SIDEBAR_LAYOUT_KEY]: SIDEBAR_LAYOUTS.includes(layout) ? layout : 'grouped',
        [SIDEBAR_GROUP_TITLES_KEY]: JSON.stringify(titles),
        [SIDEBAR_ORDER_KEY]: JSON.stringify({
            grouped: { top: groupedTop, children: groupedChildren },
            flat,
        }),
    };
}
